import { CSSProperties } from "react";

export interface VisibleArea {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Parses a CSS clip-path string and returns the visible area dimensions
 *
 * @param clipString - The clip-path CSS string (e.g., "inset(10px 20px 10px 20px)")
 * @param containerWidth - Container width in pixels (for pixel-to-percentage conversion)
 * @param containerHeight - Container height in pixels (for pixel-to-percentage conversion)
 *
 * @returns Visible area dimensions as percentages, or null if parsing fails
 */
export const parseClipPath = (
  clipString: string,
  containerWidth: number,
  containerHeight: number,
): VisibleArea | null => {
  if (!clipString) return null;

  const insetRegex = /inset\(([^)]+)\)/;
  const insetMatch = insetRegex.exec(clipString);
  if (insetMatch) {
    const values = insetMatch[1].split(/\s+/);

    if (values.length === 4) {
      const [top, right, bottom, left] = values;

      const isPixels = values.some((v) => v.includes("px"));

      if (isPixels) {
        const topPx = parseFloat(top.replace("px", ""));
        const rightPx = parseFloat(right.replace("px", ""));
        const bottomPx = parseFloat(bottom.replace("px", ""));
        const leftPx = parseFloat(left.replace("px", ""));

        return {
          top: (topPx / containerHeight) * 100,
          left: (leftPx / containerWidth) * 100,
          width: ((containerWidth - leftPx - rightPx) / containerWidth) * 100,
          height: ((containerHeight - topPx - bottomPx) / containerHeight) * 100,
        };
      } else {
        const topPct = parseFloat(top.replace("%", ""));
        const rightPct = parseFloat(right.replace("%", ""));
        const bottomPct = parseFloat(bottom.replace("%", ""));
        const leftPct = parseFloat(left.replace("%", ""));

        return {
          top: topPct,
          left: leftPct,
          width: 100 - leftPct - rightPct,
          height: 100 - topPct - bottomPct,
        };
      }
    }
  }

  return null;
};

/**
 * Creates a cropped border div element props for highlighting the visible area
 *
 * @param visibleArea - The calculated visible area dimensions
 * @param borderColor - CSS border color (defaults to confirm-review-color)
 * @param borderWidth - CSS border width (defaults to 2px)
 * @param borderRadius - CSS border radius (defaults to 4px)
 *
 * @returns Style object for the border div
 */
export const createCroppedBorderStyle = (
  visibleArea: VisibleArea,
  borderColor = "var(--confirm-review-color)",
  borderWidth = "2px",
  borderRadius = "4px",
): CSSProperties => ({
  position: "absolute",
  top: `${visibleArea.top}%`,
  left: `${visibleArea.left}%`,
  width: `${visibleArea.width}%`,
  height: `${visibleArea.height}%`,
  border: `${borderWidth} solid ${borderColor}`,
  borderRadius,
  pointerEvents: "none",
  zIndex: 1,
});
